import * as moment from 'moment'
import { isUndefined, omitBy } from 'lodash'

export class AlignerHelper {
    static readonly PHASE_NUMBERS = [
        { value: 1, name: '1st' },
        { value: 2, name: '2nd' },
        { value: 3, name: '3rd' },
        { value: 4, name: '4th' },
        { value: 5, name: '5th' },
        { value: 6, name: '6th' },
    ]

    static readonly PHASE_BY_NAMES = {
        counseling: {
            value: 'counseling',
            name: 'Counseling',
        },
        pre: {
            value: 'pre',
            name: 'Pre',
        },
        retainer: {
            value: 'retainer',
            name: 'Retainer',
        },
        followUp: {
            value: 'followUp',
            name: 'Follow Up',
        },
    }

    public static computeStates(clientAligner) {
        const listFlows = clientAligner.flows
        clientAligner = this.initialEmptyStages(clientAligner)
        let stages = [].concat(clientAligner.aligners)
        const totalNumber = clientAligner.totalNumber
        if (totalNumber === 0) {
            stages = []
        }
        // initial stage at flows
        if (listFlows && listFlows.length > 0) {
            const distinctFlow = this.convertToDistinctArr(listFlows)
            const abortFrom = clientAligner['abortedFrom']
            const arrPassedDate = this.configPassedDateScreenStage(
                clientAligner,
                totalNumber,
            )
            distinctFlow.forEach(e => {
                if (e.nextCount <= totalNumber) {
                    if (abortFrom && e.nextCount > abortFrom) {
                        stages[e.nextCount - 1] = {}
                    } else if (abortFrom && e.nextCount === abortFrom) {
                        const aligner = {
                            doctor: e.doctor,
                            staff: e.staff,
                            updateDate: arrPassedDate[e.nextCount - 1],
                            abort: true,
                        }
                        stages[e.nextCount - 1] = omitBy(
                            aligner,
                            isUndefined,
                        )
                    } else {
                        const aligner = {
                            doctor: e.doctor,
                            elasticTypeLeft: e.elasticTypeLeft,
                            elasticTypeRight: e.elasticTypeRight,
                            interval: e.interval,
                            intervalUnit: e.intervalUnit,
                            staff: e.staff,
                            updateDate: arrPassedDate[e.nextCount - 1],
                            wearingTime: e.wearingTime,
                            buttoncut: e.buttoncut,
                            iprCount: e.iprCount,
                            precisioncut: e.precisioncut,
                            treatmentOptions: e.treatmentOptions,
                            changeDate: e.changeDate,
                            changeElasticTypeLeft: e.changeElasticTypeLeft,
                            changeElasticTypeRight: e.changeElasticTypeRight,
                            changeInterval: e.changeInterval,
                            changeUpdateDate: e.changeUpdateDate,
                            changeWearingTime: e.changeWearingTime,
                        }
                        if (e.nextStartDate) {
                            aligner['startDate'] = e.nextStartDate
                            const endDate = moment(aligner['startDate'])
                            .add(
                                aligner.wearingTime,
                                aligner.intervalUnit === 'days'
                                    ? 'days'
                                    : 'months',
                            )
                            .utc()
                            .startOf('day')
                            .valueOf()
                            aligner['endDate'] = endDate
                        }
                        stages[e.nextCount - 1] = omitBy(
                            aligner,
                            isUndefined,
                        )
                    }
                }
            })

            // create stage list
            let i
            for (i = 0; i < totalNumber; i++) {
                if (JSON.stringify(stages[i]) === '{}') {
                    const stage = {}
                    const preStage = stages[i - 1]
                    if (preStage['abort']) {
                        // if canceled stage
                        delete preStage['dateDiff']
                        delete preStage['changeDate']
                        stage['updateDate'] = arrPassedDate[i]
                        stage['abort'] = true
                        stages[i] = stage
                    } else {
                        stage['elasticTypeLeft'] = preStage.elasticTypeLeft
                        stage['elasticTypeRight'] = preStage.elasticTypeRight
                        stage['interval'] = preStage.interval
                        stage['intervalUnit'] = preStage.intervalUnit
                        stage['wearingTime'] = preStage.wearingTime
                        if (preStage.startDate) {
                            const startDate = moment(preStage.startDate)
                            .add(
                                preStage.interval,
                                preStage.intervalUnit === 'days'
                                    ? 'days'
                                    : 'months',
                            )
                            .utc()
                            .startOf('day')
                            .valueOf()
                            const endDate = moment(preStage.startDate)
                            .add(
                                preStage.wearingTime,
                                preStage.intervalUnit === 'days'
                                    ? 'days'
                                    : 'months',
                            )
                            .utc()
                            .startOf('day')
                            .valueOf()
                            stage['startDate'] = startDate
                            stage['endDate'] = endDate
                        }
                        stage['updateDate'] = arrPassedDate[i]
                        stages[i] = omitBy(stage, isUndefined)
                    }
                }
                // compute date diff
                if (
                    i > 0 &&
                    stages[i - 1] &&
                    stages[i - 1]['startDate'] &&
                    !stages[i]['abort']
                ) {
                    stages[i] = this.addDateDiff(
                        stages[i],
                        stages[i - 1],
                    )
                }
            }
        }
        return stages
    }

    private static initialEmptyStages(clientAligner) {
        let i
        const tmpArr = []
        for (i = 0; i < clientAligner.totalNumber; i++) {
            tmpArr.push({})
        }
        clientAligner.aligners = tmpArr
        clientAligner.aligners[0] = {
            changeDate: true,
            changeElasticTypeLeft: true,
            changeElasticTypeRight: true,
            changeInterval: true,
            changeUpdateDate: true,
            changeWearingTime: true,
        }

        return clientAligner
    }

    private static getPassedDate(clientAligner) {
        let i
        const arrPassedDate = []
        for (i = 0; i < clientAligner.totalNumber; i++) {
            arrPassedDate.push(null)
        }
        return arrPassedDate
    }

    private static convertToDistinctArr(flows: any) {
        const tmpFlows = [].concat(flows)
        const distinctFlow = []
        tmpFlows.forEach(item => {
            const found = tmpFlows.find(e => e.nextCount === item.nextCount)
            if (distinctFlow.indexOf(found) === -1) {
                distinctFlow.push(found)
            }
        })
        return distinctFlow
    }

    private static configPassedDateScreenStage(
        clientAligner: any,
        totalNumber,
    ) {
        const listFlows = clientAligner.flows
        const arrPassedDate = this.getPassedDate(clientAligner)
        listFlows.forEach(e => {
            if (e.nextCount <= totalNumber && e.passedAligners > 0) {
                for (
                    let i = e.passedAlignerFrom - 1;
                    i <= e.passedAlignerTo - 1;
                    i++
                ) {
                    if (arrPassedDate[i] < e.updateDate) {
                        arrPassedDate.splice(i, 1, e.updateDate)
                    }
                }
            }
        })
        return arrPassedDate
    }

    private static addDateDiff(currentStage, beforeStage): any {
        const currentStageDate = moment(currentStage['startDate'])
        .utc()
        .startOf('day')
        .toDate()
        let beforeEndDate = currentStageDate
        if (beforeStage) {
            beforeEndDate = moment(beforeStage['startDate'])
            .add(
                beforeStage.interval,
                beforeStage.intervalUnit === 'days' ? 'days' : 'months',
            )
            .utc()
            .startOf('day')
            .toDate()
        }
        const dateDiff = Math.floor(
            (currentStageDate.getTime() - beforeEndDate.getTime()) / 86400000,
        )
        currentStage.dateDiff = dateDiff
        if (dateDiff !== 0) {
            currentStage.changeDate = true
        }
        return currentStage
    }
}



// WEBPACK FOOTER //
// ./src/helper/aligner.helper.ts